/**
 * Wd order status service (wd-order-status.service.ts).
 *
 * @version
 * 4.71.0 OAUI-693 Tidy and remove duplicated code
 * 4.71.0 OAUI-685 Implement tabbed sections on table
 * 4.71.0 OAUI-684 Improve shared code and add interfaces
 * 4.37.0 OAUI-527 Slowdowns in order modals
 * 4.36.0 OAUI-509 Edit order shipping option
 * 4.33.0 OAUI-468 Remove duplicate calls for constants and hostConfiguration
 * 4.26.0 OAUI-426 Add order management permissions
 * 4.26.0 OAUI-418 Hook up API calls for QR Code Order Management page
 * 4.26.0 OAUI-420 Build Basic Order Management Layout
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2021-2024 Build Centrix
 */
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { WdLocalSharedFunctionsService } from '../../shared/wd-local-shared-functions.service';
import {
  DeliveryIconTypes,
  IOrderStatusModel,
  ProductionIconTypes,
} from '../../../interfaces/statuses/order-status-interfaces';

@Injectable({
  providedIn: 'root',
})
export class WdOrderStatusService {
  // Booleans
  isLoading = new BehaviorSubject(false);

  // Statuses
  orderItemShippingStatusOptions = new BehaviorSubject<Array<any>>([]);
  overallStatusOptions = new BehaviorSubject<Array<any>>([]);
  productionStatusOptions = new BehaviorSubject<Array<any>>([]);
  shippingStatusOptions = new BehaviorSubject<Array<any>>([]);

  /**
   * Constructor
   *
   * @since 4.26.0
   */
  constructor(
    public wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    @Inject('wdErrorService') public wdErrorService: any,
    @Inject('wdOauth2HttpService') public wdOauth2HttpService: any,
    @Inject('wdSharedFunctionsService') public wdSharedFunctionsService: any,
  ) {}

  /**
   * Get Delivery Status Type by Key
   * Returns a type which is used to display a Font Awesome icon via style guide
   * @since 4.71.0
   * @memberof WdOrderStatusService
   */
  public static getDeliveryTypeByStatus(status: IOrderStatusModel): string {
    const statusIndex = status?.index;

    // Enum Mapper for Delivery Icons
    const DELIVERY_ICON_TYPE_MAPPER: { [key: number]: DeliveryIconTypes } = {
      0: DeliveryIconTypes.DEFAULT,
      1: DeliveryIconTypes.NOT_STARTED,
      2: DeliveryIconTypes.SHIPPED,
      3: DeliveryIconTypes.CANCELLED,
      4: DeliveryIconTypes.PARTIALLY_SHIPPED,
      5: DeliveryIconTypes.ON_HOLD,
    };

    // If no icon specified on the delivery status, then fallback to use the hard-coded icon mappings
    const fallbackIcon =
      DELIVERY_ICON_TYPE_MAPPER[statusIndex] || DELIVERY_ICON_TYPE_MAPPER[0];

    // Check first for an icon in MySQL ofs_order_delivery_status.iconId.  If none found, use the fallback hard-coded icons.
    return status?.iconId ? status?.iconId : fallbackIcon;
  }

  /**
   * Get Production Status Type by Key
   * Returns a type which is used to display a Font Awesome icon via style guide
   * @since 4.71.0
   * @memberof WdOrderStatusService
   */
  static getProductionTypeByStatus(status: IOrderStatusModel): string {
    const statusIndex = status?.index;

    // Enum Mapper for Production Icons
    const PRODUCTION_ICON_TYPE_MAPPER: {
      [key: number]: ProductionIconTypes;
    } = {
      0: ProductionIconTypes.DEFAULT,
      1: ProductionIconTypes.NOT_STARTED,
      2: ProductionIconTypes.IN_PRODUCTION,
      3: ProductionIconTypes.READY,
      5: ProductionIconTypes.CANCELLED,
      7: ProductionIconTypes.ON_HOLD,
    };

    // If no icon specified on the production status, then fallback to use the hard-coded icon mappings
    const fallbackIcon =
      PRODUCTION_ICON_TYPE_MAPPER[statusIndex] ||
      PRODUCTION_ICON_TYPE_MAPPER[0];

    // Check first for an icon in MySQL ofs_order_item_status.iconId.  If none found, use the fallback hard-coded icons.
    return status?.iconId ? status?.iconId : fallbackIcon;
  }

  /**
   * Parse Order Item Shipping Status
   * @since 4.26.0
   */
  static parseOrderItemShippingStatus(status: any): object {
    return {
      id: status.id,
      label: status.label,
      sort: status.sort,
    };
  }

  /**
   * Parse Order Status
   * @since 4.26.0
   */
  static parseOrderStatus(status: any): object {
    const result: any = {
      id: status.id,
      label: status.label,
    };
    if (status.type) {
      result.type = status.type;
    }
    return result;
  }

  /**
   * Parse Production Status
   *
   * @since 4.26.0
   */
  static parseProductionStatus(status: any): object {
    return {
      id: parseInt(status.index),
      label: status.code,
    };
  }

  /**
   * Parse Shipping Status
   *
   * @since 4.26.0
   */
  static parseShippingStatus(status: any): object {
    return {
      id: parseInt(status.index),
      label: status.label,
    };
  }

  /**
   * Parse Order Statuses
   * @since 4.26.0
   * @param statuses
   */
  parseOrderItemShippingStatuses(statuses: any): Array<any> {
    return statuses.map((status: any) =>
      WdOrderStatusService.parseOrderItemShippingStatus(status),
    );
  }

  /**
   * Parse Order Statuses
   * @since 4.26.0
   * @param statuses
   */
  parseOrderStatuses(statuses: any): Array<any> {
    return statuses.map((status: any) =>
      WdOrderStatusService.parseOrderStatus(status),
    );
  }

  /**
   * Parse Production Statuses
   * @since 4.26.0
   * @param statuses
   */
  parseProductionStatuses(statuses: any): Array<any> {
    return statuses.map((status: any) =>
      WdOrderStatusService.parseProductionStatus(status),
    );
  }

  /**
   * Parse Shipping Statuses
   * @since 4.26.0
   * @param statuses
   */
  parseShippingStatuses(statuses: any): Array<any> {
    return statuses.map((status: any) =>
      WdOrderStatusService.parseShippingStatus(status),
    );
  }

  /**
   * GET order item shipping statuses
   *
   * @since 4.26.0
   * @memberof WdOrderStatusService
   */
  async getOrderItemShippingStatuses(): Promise<any> {
    // Toggle Loader
    this.isLoading.next(true);
    try {
      const params = 'getByTokenEnvironment=' + true;
      const response = await this.wdOauth2HttpService.get(
        `${
          this.wdLocalSharedFunctions.orderApiUrl
        }orders/statuses/items/shipping${params ? '?' + params : ''}`,
      );
      this.orderItemShippingStatusOptions.next(
        this.parseOrderItemShippingStatuses(response.data),
      );
      return this.orderItemShippingStatusOptions.value;
    } catch (error) {
      this.wdErrorService.userError(
        'Error',
        `Error getting item order statuses`,
      );
      throw error;
    } finally {
      // Toggle Loader
      this.isLoading.next(false);
    }
  }

  /**
   * GET order statuses
   *
   * @since 4.26.0
   * @memberof WdOrderStatusService
   */
  async getOrderStatuses(): Promise<any> {
    // Toggle Loader
    this.isLoading.next(true);
    try {
      const response = await this.wdOauth2HttpService.get(
        `${this.wdLocalSharedFunctions.orderingApiUrl}orders/statuses`,
      );
      this.overallStatusOptions.next(this.parseOrderStatuses(response.data));
      return this.overallStatusOptions.value;
    } catch (error) {
      this.wdErrorService.userError('Error', `Error getting order statuses`);
      throw error;
    } finally {
      // Toggle Loader
      this.isLoading.next(false);
    }
  }

  /**
   * GET production statuses
   *
   * @since 4.26.0
   * @memberof WdOrderStatusService
   */
  async getProductionStatuses(): Promise<any> {
    // Toggle Loader
    this.isLoading.next(true);
    try {
      const response = await this.wdOauth2HttpService.get(
        `${this.wdLocalSharedFunctions.orderingApiUrl}orders/fulfillment/statuses`,
      );
      this.productionStatusOptions.next(
        this.parseProductionStatuses(response.data),
      );
      return this.productionStatusOptions.value;
    } catch (error) {
      this.wdErrorService.userError(
        'Error',
        `Error getting production statuses`,
      );
      throw error;
    } finally {
      // Toggle Loader
      this.isLoading.next(false);
    }
  }

  /**
   * GET shipping statuses
   *
   * @since 4.26.0
   * @memberof WdOrderStatusService
   */
  async getShippingStatuses(): Promise<any> {
    // Toggle Loader
    this.isLoading.next(true);
    try {
      const response = await this.wdOauth2HttpService.get(
        `${this.wdLocalSharedFunctions.orderingApiUrl}orders/delivery/statuses`,
      );
      this.shippingStatusOptions.next(
        this.parseShippingStatuses(response.data),
      );
      return this.shippingStatusOptions.value;
    } catch (error) {
      this.wdErrorService.userError('Error', `Error getting shipping statuses`);
      throw error;
    } finally {
      // Toggle Loader
      this.isLoading.next(false);
    }
  }
}
